import { toIntOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import type { INowFormValues, INowModel, INowSubmitResult } from '@/types/now';

const NOW_ENDPOINT = '/api/register/now';
const NOW_MICROSITE_ENDPOINT = '/api/register/now-microsite';

const defaultNowModel: INowModel = {
    isRegistrationEnabled: true,
    isIE: false,
    isModelValid: true,
    isPrivateLabel: false,
    micrositeRecipient: null,
    isDashLiverAvailable: false,
    isLiverMicrosite: false,
    organType: 0,

    id: '',
    editKey: '',
    entryQuery: '',
    isFromPedGallery: false,

    recaptchaKey: '',
    language: 'en',
    googleAnalyticsCode: null,

    languageData: {},

    donationTypes: [],
    states: [],
    countries: [],

    hasDynamicRegPage: false,
    isGenericRegPage: false,
    heroImageUrl: null,
    heroTextOverlay: '',
    heroTextOverlayAlignment: 'left',
    ribbonTwoMediaUrl: null,
    ribbonTwoTitleHtml: '',
    ribbonTwoIntroHtml: '',
    ribbonTwoBodyHtml: '',

    errors: {},
    values: {},
};

interface INowResults {
    token: string;
}

interface INowSubmitValues {
    values: INowFormValues;
    recaptchaToken: string;
}

export const useNow = (forMicrosite?: boolean) => {
    const urlParams = new URLSearchParams(window.location.search);
    const src: string | null = urlParams.get('src');
    const micrositeId: string | null = urlParams.get('id');

    const url =
        forMicrosite == true
            ? NOW_MICROSITE_ENDPOINT + (micrositeId ? '?micrositeId=' + micrositeId : '')
            : NOW_ENDPOINT + '?src=' + (src ? src : '');

    const { model, isLoading, error } = useApiModel(url, defaultNowModel);

    const { submit: post, isSubmitting, submitError } = useApiSubmit<INowSubmitValues, INowResults, INowSubmitResult>(
        NOW_ENDPOINT,
        ({ values, recaptchaToken }) => ({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            isResidingInUsa: values.isResidingInUsa !== '' ? values.isResidingInUsa === '1' : null,
            countryOfResidenceId: values.countryOfResidenceId ? parseInt(values.countryOfResidenceId) : null,
            internationalPhoneNumber: values.internationalPhoneNumber,
            addressLine1: values.addressLine1,
            city: values.city,
            state: values.state ? parseInt(values.state) : null,
            postalCode: values.postalCode,
            mobileAuthPhone: values.mobileAuthPhone,
            donationType: toIntOrNull(values.donationType),
            agreedToHipaa: values.agreedToHipaa,
            connectToDonorIsDesired: values.connectToDonorIsDesired !== '' ? values.connectToDonorIsDesired === '1' : null,
            id: model.id,
            editKey: model.editKey,
            entryQuery: window.location.search.replace(/^\?/, ''),
            isFromPedGallery: model.isFromPedGallery,
            micrositeRecipientId: model.micrositeRecipient != null ? model.micrositeRecipient.id : null,
            isDashLiverAvailable: model.isDashLiverAvailable,
            recaptchaToken,
        }),
        (results) => ({ token: results.token }),
    );

    const submit = (values: INowFormValues, recaptchaToken: string): Promise<INowSubmitResult> => post({ values, recaptchaToken });

    return { model, isLoading, error, submit, isSubmitting, submitError };
};
